import React, { useState, useEffect, useContext } from "react";
import { Slide } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";
import {
  Box,
  Typography,
  Container,
  Skeleton,
  Grid,
  Button,
} from "@mui/material";
import { AuthContext } from "context/Authentication";
import { postRecord } from "services/services";
import { useNavigate } from "react-router-dom";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import ServiceCardDefault from "CommonComponents/ServiceCard/ServiceCardDefault.jsx";
import ServiceCardDigital from "CommonComponents/ServiceCard/ServiceCardDigital.jsx";

const API_GetAllServices = "services/getall";
const API_GetServiceMedia = "servicesmedia/getbyserviceid";
const API_GetUserSubscriptions = "user_subscriptions/getbyuserid";

export default function PopularServices({ headerRefresh, setHeaderRefresh }) {
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  const isAudioBook =
    context.state?.companyInfo?.Product_Service_Type === "Product - Digital";

  const [popularServices, setPopularServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latestActiveSubscription, setLatestActiveSubscription] =
    useState(null);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]); // Store all active subscriptions

  useEffect(() => {
    let isMounted = true;

    const fetchServices = async () => {
      try {
        const response = await postRecord(API_GetAllServices, {});
        if (isMounted) {
          if (response.status === 200 && response.data) {
            // Filter services that are on homepage
            const popular = response.data.filter(
              (service) => service.On_Homepage === "1"
            );

            // Get media for each popular service
            const servicesWithMedia = await Promise.all(
              popular.map(async (service) => {
                const mediaResponse = await postRecord(API_GetServiceMedia, {
                  Service_ID: service.Service_Id,
                });
                let thumbnailImage = null;
                if (mediaResponse.status === 200 && mediaResponse.data) {
                  if (isAudioBook) {
                    // For digital, use Thumbnail
                    const thumbnail = mediaResponse.data.find(
                      (media) => media.Media_Tag === "Thumbnail"
                    );
                    if (thumbnail) {
                      thumbnailImage = thumbnail.Media_File_Path;
                    }
                  } else {
                    // For others, use first image if available
                    if (mediaResponse.data.length > 0) {
                      thumbnailImage = mediaResponse.data[0].Media_File_Path;
                    }
                  }
                }
                return {
                  ...service,
                  thumbnailImage,
                  // Add a random rating for display purposes
                  rating: (4 + Math.random()).toFixed(1),
                };
              })
            );

            setPopularServices(servicesWithMedia);
          }
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching popular services:", error);
          setLoading(false);
        }
      }
    };

    fetchServices();

    // Fetch user subscriptions if isAudioBook and user is logged in
    if (isAudioBook && context.state.user?.User_Id) {
      postRecord(API_GetUserSubscriptions, {
        User_Id: context.state.user.User_Id,
      }).then((resp) => {
        if (resp.status === 200 && Array.isArray(resp.data)) {
          // Filter only active subscriptions
          const activeSubs = resp.data.filter(
            (sub) =>
              sub.Subscription_Status === "active" &&
              sub.Payment_Status === "Paid" &&
              sub.Is_Cancelled === "0"
          );
          // Sort by Added_On descending (latest first)
          activeSubs.sort(
            (a, b) =>
              new Date(b.Added_On).getTime() - new Date(a.Added_On).getTime()
          );
          setLatestActiveSubscription(
            activeSubs.length > 0 ? activeSubs[0] : null
          );
          setActiveSubscriptions(activeSubs); // Store all active subscriptions
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // Helper: check if service is covered by any active subscription
  const isServiceSubscribed = (service) => {
    if (!service || !activeSubscriptions.length) return false;
    const tags = (service.Service_Tags || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    return activeSubscriptions.some((sub) => {
      const planName =
        sub.SubscriptionPlan?.SubscriptionPlan_Name?.toLowerCase();
      return planName && tags.includes(planName);
    });
  };

  const renderSkeletonSlider = () => (
    <Grid container spacing={1}>
      {[...Array(5)].map((_, index) => (
        <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2.4} key={index}>
          <Box sx={{ width: "100%", p: 1 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={140}
              animation="wave"
              sx={{ borderRadius: "8px" }}
            />
            <Box sx={{ pt: 1 }}>
              <Skeleton animation="wave" height={24} width="70%" />
              <Skeleton animation="wave" height={16} width="40%" />
              <Skeleton animation="wave" height={20} width="90%" />
              <Skeleton
                animation="wave"
                height={20}
                width="40%"
                sx={{ mt: 1 }}
              />
              <Skeleton
                animation="wave"
                height={36}
                width="60%"
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  // Responsive settings for the slider - adjusted to handle low item counts
  const getResponsiveSettings = (itemCount) => {
    return [
      {
        breakpoint: 1900,
        settings: { slidesToShow: Math.min(5, itemCount), slidesToScroll: 1 },
      },
      {
        breakpoint: 1200,
        settings: { slidesToShow: Math.min(4, itemCount), slidesToScroll: 1 },
      },
      {
        breakpoint: 800,
        settings: { slidesToShow: Math.min(3, itemCount), slidesToScroll: 1 },
      },
      {
        breakpoint: 500,
        settings: { slidesToShow: Math.min(2, itemCount), slidesToScroll: 1 },
      },
      { breakpoint: 0, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ];
  };

  const handleDigitalCardClick = (service) => {
    navigate(`/ServiceView/${service.Service_Id}`);
  };

  const handleBookNow = (service) => {
    if (isAudioBook) {
      navigate(`/ServiceView/${service.Service_Id}`);
    } else {
      const shortNameParam = (
        service.Service_Short_Name || service.Service_Full_Name
      )
        .toLowerCase()
        .replace(/ /g, "-");
      navigate(`/ServicesWithVendors/${shortNameParam}`);
    }
  };

  // Button logic for audiobooks
  const getAudioBookButton = (service) => {
    // Check if this service is covered by any active subscription
    const hasActiveSubscription = isServiceSubscribed(service);
    if (hasActiveSubscription) {
      return (
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2, alignSelf: "flex-end" }}
          startIcon={<BookOnlineIcon />}
          onClick={() => navigate(`/AudioPlayer/${service.Service_Id}`)}
        >
          Listen Now
        </Button>
      );
    } else {
      return (
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2, alignSelf: "flex-end" }}
          startIcon={<BookOnlineIcon />}
          onClick={() => navigate("/SubscriptionList")}
        >
          Subscribe
        </Button>
      );
    }
  };

  // Utility: group services by Service_Short_Name
  function groupServicesByShortName(services) {
    const groups = {};
    services.forEach((service) => {
      const key = service.Service_Short_Name || service.Service_Full_Name;
      if (!groups[key]) groups[key] = [];
      groups[key].push(service);
    });
    return Object.values(groups).map((group) => {
      // Sort by price ascending
      const sorted = group
        .slice()
        .sort(
          (a, b) => parseFloat(a.Selling_Price) - parseFloat(b.Selling_Price)
        );
      const first = sorted[0];
      return {
        ...first,
        Service_Ids: group.map((s) => s.Service_Id),
        minPrice: sorted[0]?.Selling_Price,
        thumbnailImage: first.thumbnailImage,
        rating: first.rating,
      };
    });
  }

  // Check if Service Collection is enabled
  const isServiceCollection = Array.isArray(context.state?.appSettings)
    ? context.state.appSettings.some(
        (s) =>
          s.Setting_Name === "Service Collection" &&
          s.Setting_Value === "Enabled"
      )
    : false;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography
        variant="h4"
        align="center"
        sx={{ fontWeight: "bold", mb: 1 }}
      >
        {isAudioBook ? "Popular Audio Books" : "Popular Services"}
      </Typography>
      <Box
        sx={{
          width: "80px",
          height: "2px",
          backgroundColor: "primary.main",
          mx: "auto",
          mb: 2,
        }}
      />

      {loading ? (
        renderSkeletonSlider()
      ) : popularServices.length > 0 ? (
        (() => {
          const displayServices =
            isServiceCollection && !isAudioBook
              ? groupServicesByShortName(popularServices)
              : popularServices;
          return displayServices.length > 4 ? (
            <Grid container spacing={1} justifyContent="center">
              <Grid item xs={12}>
                <Box
                  sx={{
                    "& .react-slideshow-container .react-slideshow-wrapper": {
                      margin: "0 auto",
                      maxWidth: "100%",
                    },
                    "& .react-slideshow-container .react-slideshow-wrapper .images-wrap > div":
                      {
                        maxWidth: { xs: "100%", sm: "280px" },
                        margin: "0 auto",
                      },
                  }}
                >
                  <Slide
                    responsive={getResponsiveSettings(displayServices.length)}
                    indicators={true}
                    infinite={true}
                  >
                    {displayServices.map((service) => (
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        key={service.Service_Id}
                        sx={{ height: "100%" }}
                      >
                        <Grid
                          item
                          xs={12}
                          sx={{ display: "flex", justifyContent: "center" }}
                        >
                          {isAudioBook ? (
                            <ServiceCardDigital
                              service={service}
                              onClick={handleDigitalCardClick}
                              buttonComponent={getAudioBookButton(service)}
                              isServiceCollection={isServiceCollection}
                              isSlider={true}
                              maxWidth="280px"
                            />
                          ) : (
                            <ServiceCardDefault
                              service={service}
                              onBookNow={handleBookNow}
                              isServiceCollection={isServiceCollection}
                              maxWidth="280px"
                            />
                          )}
                        </Grid>
                      </Grid>
                    ))}
                  </Slide>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              spacing={1}
              justifyContent="center"
              alignItems="stretch"
            >
              {displayServices.map((service) =>
                isAudioBook ? (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2.4}
                    xl={2.4}
                    key={service.Service_Id}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <ServiceCardDigital
                      service={service}
                      onClick={handleDigitalCardClick}
                      buttonComponent={getAudioBookButton(service)}
                      isServiceCollection={isServiceCollection}
                      isSlider={false}
                    />
                  </Grid>
                ) : (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2.4}
                    xl={2.4}
                    key={service.Service_Id}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <ServiceCardDefault
                      service={service}
                      onBookNow={handleBookNow}
                      isServiceCollection={isServiceCollection}
                    />
                  </Grid>
                )
              )}
            </Grid>
          );
        })()
      ) : (
        <Typography variant="body1" align="center">
          No popular services available
        </Typography>
      )}
    </Container>
  );
}
